import {
  type PayloadAction,
  type SerializedError,
  createSlice,
  nanoid,
} from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { invalidateTags } from 'api/baseApi';
import { getRtkErrorMessage } from 'api/getRtkErrorMessage';
import { fieldTypeTag, fieldsApi } from 'pages/objectManager/api/fieldsApi';
import { objectApi } from 'pages/objectManager/api/objectApi';
import {
  addObjectToLists,
  removeObjectFromLists,
  replaceObjectInLists,
} from 'pages/objectManager/api/objectListCache';
import { buildObjectSavePayload } from 'pages/objectManager/utils/buildObjectSavePayload';
import {
  type ObjectManagerDirtyFields,
  getUpdatedDirtyFields,
} from 'pages/objectManager/utils/dirtyFields';
import { orderedFieldIdsForTypes } from 'pages/objectManager/utils/fieldOrder';
import { fetchFieldsInGlobalOrder } from 'slices/field.slice';
import { startAppListening } from 'slices/listenerMiddleware';
import type { TAppThunk, TRootState } from 'slices/store';
import { checkSavedDisabled } from 'utils/isSavedDisabled';
import { type TListKeyers, mergeDrafts } from 'utils/mergeDrafts';
import { message } from 'utils/messageUtils';
import { errorNotification } from 'utils/notifications';
import {
  NEW_OBJECT_ID,
  collectOrderedFieldIds,
  isNewObject,
  mergeFieldsForTypes,
} from 'utils/objectUtils';
import { type TTEObject, createEmptyTEObject } from '@timeedit/types';

export type TDrawerKey = 'first' | 'second';

// `base` is the version the draft started from: the server copy, or the template of a new object.
// `conflicts` names what a page update found changed both here and on the server (mergeDrafts).
export type TDrawerEdit = {
  base: TTEObject;
  draft: TTEObject;
  dirtyFields: ObjectManagerDirtyFields;
  conflicts: string[];
  deleted: boolean;
  // The latest type change while it waits for its types' fields: nothing can be saved
  // meanwhile, Discard drops it, and an older change or one started on an object this edit
  // has since replaced is dropped when it lands.
  typeChange: string | null;
};

export type TRemoteChange = 'conflict' | 'deleted';

// How a draft's lists merge with a newer server version: fields by field, associations as sets
const OBJECT_LISTS: TListKeyers<TTEObject> = {
  fields: (field) => String(field.fieldId),
  types: String,
  organizations: String,
  optionalRelations: String,
  members: (member) => String(member.objectId),
  relations: (relation) => JSON.stringify(relation),
  availabilityRelations: (relation) => String(relation.availabilityTypeId),
};

const DRAWER_KEYS: TDrawerKey[] = ['first', 'second'];

export type TObjectEditorState = Record<TDrawerKey, TDrawerEdit | null> & {
  // Objects created since the list was last loaded; they sit at the top until then
  createdIds: number[];
};

const initialState: TObjectEditorState = {
  first: null,
  second: null,
  createdIds: [],
};

// An empty text value is [] from the form and [''] from the server; neither is an edit
const comparable = (object: TTEObject): TTEObject => ({
  ...object,
  fields: object.fields.map((field) => ({
    ...field,
    values: field.values.filter((value) => value !== ''),
  })),
});

const opened = (object: TTEObject): TDrawerEdit => ({
  base: object,
  draft: object,
  dirtyFields: {},
  conflicts: [],
  deleted: false,
  typeChange: null,
});

const slice = createSlice({
  name: 'objectEditor',
  initialState,
  reducers: {
    objectOpened: (
      state,
      { payload }: PayloadAction<{ key: TDrawerKey; object: TTEObject }>
    ) => {
      state[payload.key] = opened(payload.object);
    },
    newObjectCreated: (
      state,
      { payload }: PayloadAction<{ typeId: number; fieldIds: number[] }>
    ) => {
      state.first = opened(
        createEmptyTEObject(payload.typeId, payload.fieldIds)
      );
    },
    objectDuplicated: (
      state,
      { payload: object }: PayloadAction<TTEObject>
    ) => {
      state.first = opened({
        ...object,
        id: NEW_OBJECT_ID,
        extId: object.extId + '-copy',
      });
    },
    draftChanged: (
      state,
      {
        payload: { key, patch },
      }: PayloadAction<{ key: TDrawerKey; patch: Partial<TTEObject> }>
    ) => {
      const edit = state[key];
      if (!edit) return;
      edit.dirtyFields = getUpdatedDirtyFields(
        patch,
        edit.base,
        edit.dirtyFields
      );
      edit.draft = { ...edit.draft, ...patch };
    },
    typeChangeStarted: (
      state,
      {
        payload: { key, token },
      }: PayloadAction<{ key: TDrawerKey; token: string }>
    ) => {
      const edit = state[key];
      if (edit) edit.typeChange = token;
    },
    typeChangeEnded: (state, { payload: key }: PayloadAction<TDrawerKey>) => {
      const edit = state[key];
      if (edit) edit.typeChange = null;
    },
    draftReset: (state, { payload: key }: PayloadAction<TDrawerKey>) => {
      const edit = state[key];
      if (!edit) return;
      edit.draft = edit.base;
      edit.dirtyFields = {};
      edit.conflicts = [];
      edit.typeChange = null;
    },
    // The server version of an open object, read on a page update or by a poll. Without edits the
    // drawer takes it as is; with edits they are merged on top and what clashes is reported.
    serverVersionReceived: (
      state,
      {
        payload: { key, id, object },
      }: PayloadAction<{ key: TDrawerKey; id: number; object: TTEObject | null }>
    ) => {
      const edit = state[key];
      if (!edit || edit.draft.id !== id) return;
      if (object === null) {
        edit.deleted = true;
        return;
      }
      if (checkSavedDisabled(edit.draft, edit.base)) {
        state[key] = opened(object);
        return;
      }
      const server = comparable(object);
      const { merged, conflicts } = mergeDrafts(
        comparable(edit.base),
        comparable(edit.draft),
        server,
        OBJECT_LISTS
      );
      edit.base = server;
      edit.draft = merged;
      edit.conflicts = conflicts;
      edit.deleted = false;
      edit.dirtyFields = getUpdatedDirtyFields(merged, object, {});
    },
    drawerClosed: (state, { payload: key }: PayloadAction<TDrawerKey>) => {
      state[key] = null;
    },
    drawersClosedForObject: (state, { payload: id }: PayloadAction<number>) => {
      if (state.first?.draft.id === id) state.first = null;
      if (state.second?.draft.id === id) state.second = null;
    },
    objectCreated: (state, { payload: id }: PayloadAction<number>) => {
      state.createdIds.push(id);
    },
    createdIdsCleared: (state) => {
      state.createdIds = [];
    },
  },
});

export default slice.reducer;

export const {
  objectOpened,
  newObjectCreated,
  objectDuplicated,
  draftChanged,
  typeChangeStarted,
  typeChangeEnded,
  draftReset,
  serverVersionReceived,
  drawerClosed,
  drawersClosedForObject,
  objectCreated,
  createdIdsCleared,
} = slice.actions;

export const selectCreatedIds = (state: TRootState) =>
  state.objectEditor.createdIds;

export const selectDrawerEdit = (state: TRootState, key: TDrawerKey) =>
  state.objectEditor[key];

export const selectDraft = (state: TRootState, key: TDrawerKey) =>
  state.objectEditor[key]?.draft;

export const selectIsSaveDisabled = (state: TRootState, key: TDrawerKey) => {
  const edit = state.objectEditor[key];
  // A type change still loading its fields would be left out of the save
  return (
    !edit ||
    edit.typeChange != null ||
    checkSavedDisabled(edit.draft, edit.base)
  );
};

export const selectRemoteChange = (
  state: TRootState,
  key: TDrawerKey
): TRemoteChange | undefined => {
  const edit = state.objectEditor[key];
  if (!edit || isNewObject(edit.draft)) return undefined;
  if (edit.deleted) return 'deleted';
  return edit.conflicts.length > 0 ? 'conflict' : undefined;
};

// The conflicts as the user knows them: field names for `fields.<id>`, the property otherwise
export const conflictLabels = (
  conflicts: readonly string[],
  fieldMap: TRootState['fields']['fieldMapping']
) =>
  conflicts.map((conflict) => {
    const [kind, itemKey] = conflict.split('.');
    if (kind !== 'fields' || itemKey === undefined) return kind;
    return fieldMap[Number(itemKey)]?.name ?? itemKey;
  });

export const selectConflictLabels = (state: TRootState, key: TDrawerKey) =>
  conflictLabels(
    state.objectEditor[key]?.conflicts ?? [],
    state.fields.fieldMapping
  );

export const selectEditorBaseById = (state: TRootState, id: number) =>
  [state.objectEditor.first, state.objectEditor.second].find(
    (edit) => edit?.draft.id === id
  )?.base;

// Changing types re-derives which fields the object has, keeping values the user entered
export const advanceUpdateObject =
  (key: TDrawerKey, patch: Partial<TTEObject>): TAppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    if (!patch.types || !getState().objectEditor[key]) {
      dispatch(draftChanged({ key, patch }));
      return;
    }
    const token = nanoid();
    dispatch(typeChangeStarted({ key, token }));

    // Field metadata for every type must be loaded before the fields can be merged
    await Promise.all(
      patch.types.map((typeId) =>
        dispatch(
          fieldsApi.endpoints.getFieldsOfType.initiate(typeId, {
            subscribe: false,
          })
        )
      )
    );
    let orderedFieldIds = orderedFieldIdsForTypes(
      patch.types,
      getState().objectTypes.map,
      getState().fields.orderByType,
      getState().fields.globalOrder
    );
    if (!orderedFieldIds) {
      // Types that don't nest need the order across all fields; rare, so loaded only now
      await dispatch(fetchFieldsInGlobalOrder());
      orderedFieldIds = collectOrderedFieldIds(
        patch.types,
        getState().objectTypes.map,
        getState().fields.globalOrder
      );
    }

    // Meanwhile the drawer may have closed, shown another object or started a newer change
    const edit = getState().objectEditor[key];
    if (edit?.typeChange !== token) return;
    const mergedFields = mergeFieldsForTypes(
      orderedFieldIds,
      edit.draft.fields,
      edit.base.fields,
      getState().fields.fieldMapping
    );
    dispatch(draftChanged({ key, patch: { ...patch, fields: mergedFields } }));
    dispatch(typeChangeEnded(key));
  };

const DEFAULT_ERROR = 'An error occurred while processing the request.';

// unwrap() rejects with FetchBaseQueryError or SerializedError; the latter has only optional
// props, so any object qualifies
const isRtkError = (
  error: unknown
): error is FetchBaseQueryError | SerializedError =>
  typeof error === 'object' && error !== null;

const notifyError = (error: unknown) => {
  console.error(error);
  const text = isRtkError(error) ? getRtkErrorMessage(error) : DEFAULT_ERROR;
  errorNotification(text === 'Unknown error' ? DEFAULT_ERROR : text);
};

// The row shows what the server saved, read once by id (or by Ext. Id for a new object, which
// gets its id from the server). The list itself is not reloaded.
const showSavedObject =
  (draft: TTEObject): TAppThunk<Promise<void>> =>
  async (dispatch) => {
  const isNew = draft.id === NEW_OBJECT_ID;
  let saved: TTEObject | null;
  try {
    saved = isNew
      ? ((
          await dispatch(
            objectApi.endpoints.findObjects.initiate(
              {
                extIds: [draft.extId],
                includeReferenceFields: true,
                includeTemplateObjects: true,
                limit: 1,
              },
              { subscribe: false, forceRefetch: true }
            )
          ).unwrap()
        ).results[0] ?? null)
      : await dispatch(
          objectApi.endpoints.getObjectById.initiate(draft.id, {
            subscribe: false,
            forceRefetch: true,
          })
        ).unwrap();
  } catch {
    // The save went through; without the server version the row shows what was sent
    saved = isNew ? null : draft;
  }
  if (isNew) {
    if (saved) {
      dispatch(addObjectToLists(saved));
      dispatch(objectCreated(saved.id));
    }
  } else if (saved) {
    dispatch(replaceObjectInLists(saved));
  } else {
    dispatch(removeObjectFromLists(draft.id));
  }
};

export const saveDrawerObject =
  (key: TDrawerKey): TAppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const edit = getState().objectEditor[key];
    if (!edit) return;
    const { draft } = edit;
    const body = buildObjectSavePayload(draft, getState().fields.fieldMapping);
    try {
      await (draft.id === NEW_OBJECT_ID
        ? dispatch(objectApi.endpoints.createObject.initiate(body)).unwrap()
        : dispatch(
            objectApi.endpoints.updateObject.initiate({ id: draft.id, body })
          ).unwrap());
    } catch (error) {
      notifyError(error);
      return;
    }
    message.success('The operation was successful');
    dispatch(drawerClosed(key));
    await dispatch(showSavedObject(draft));
    // The types' fields are read again, so category options stay current
    dispatch(invalidateTags(draft.types.map(fieldTypeTag)));
  };

export const deleteDrawerObject =
  (id: number): TAppThunk<Promise<void>> =>
  async (dispatch) => {
    try {
      await dispatch(objectApi.endpoints.deleteObject.initiate(id)).unwrap();
    } catch (error) {
      notifyError(error);
      return;
    }
    message.success('The operation was successful');
    dispatch(drawersClosedForObject(id));
    dispatch(removeObjectFromLists(id));
  };

// Reads every open object again after the list reloaded; see serverVersionReceived for what
// happens to edits. A failed read leaves the drawer as it is; the next poll raises the banner again.
export const refreshDrawers =
  (): TAppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    await Promise.all(
      DRAWER_KEYS.map(async (key) => {
        const edit = getState().objectEditor[key];
        if (!edit || isNewObject(edit.draft)) return;
        const id = edit.draft.id;
        try {
          const object = await dispatch(
            objectApi.endpoints.getObjectById.initiate(id, {
              subscribe: false,
              forceRefetch: true,
            })
          ).unwrap();
          dispatch(serverVersionReceived({ key, id, object }));
        } catch {
          // Nothing to show; the drawer keeps what it has
        }
      })
    );
  };

// For callbacks that must read the drawer state at click time rather than from a closure
export const checkDrawerSaveDisabled =
  (key: TDrawerKey): TAppThunk<boolean> =>
  (_, getState) =>
    selectIsSaveDisabled(getState(), key);

export const checkDrawerOpen =
  (key: TDrawerKey): TAppThunk<boolean> =>
  (_, getState) =>
    getState().objectEditor[key] != null;

// Any read that finds an open object gone marks its drawers, so Save is blocked at once
startAppListening({
  matcher: objectApi.endpoints.getObjectById.matchFulfilled,
  effect: ({ payload, meta }, { dispatch, getState }) => {
    if (payload !== null) return;
    const id = meta.arg.originalArgs;
    for (const key of DRAWER_KEYS) {
      if (getState().objectEditor[key]?.draft.id === id) {
        dispatch(serverVersionReceived({ key, id, object: null }));
      }
    }
  },
});
